import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Button';

import { UserRole } from 'core/constants';

import { useStore } from 'utils/hooks/store';

import { findUnit, isIntake, unitPath } from 'containers/OrganisationStructure/types';

import routes from 'core/routes';

import ChangeUnitModal from './ChangeUnitModal';
import RolePicker from './RolePicker';
import {
  AccessRowActions,
  AccessRowMeta,
  AccessRowShell,
  AccessRowText,
  AccessRowTitle,
  AccessRowTitleLine,
  Muted,
  RoleList,
  RolesBlock,
  RolesFooter,
} from './styles';

const RolesAtUnits: React.FunctionComponent = () => {
  const { session, userDetails, organisationStructure } = useStore();
  const navigate = useNavigate();
  const [changing, setChanging] = React.useState(false);

  React.useEffect(() => {
    organisationStructure.loadUnits(false);
  }, [organisationStructure]);

  const units = organisationStructure.tree;
  const details = userDetails.details;
  if (!details) return null;

  const isOwner = session.user?.role === UserRole.AccountManager;
  const canManage = isOwner && details.isActive;
  const loaded = organisationStructure.surfaceState === 'default';
  const hasStructure = (units[0]?.children ?? []).some((unit) => !isIntake(unit));
  const placed = Boolean(details.homeUnit);
  const unitUuid = details.homeUnit && findUnit(units, details.homeUnit) ? details.homeUnit : null;

  return (
    <RolesBlock>
      <RoleList aria-label={`${details.name}'s roles`}>
        {unitUuid ? (
          <AccessRowShell data-testid="role-row-home-unit">
            <AccessRowText>
              <AccessRowTitleLine>
                <RolePicker />
                <AccessRowTitle>{`at ${unitPath(units, unitUuid)}`}</AccessRowTitle>
              </AccessRowTitleLine>
              <AccessRowMeta>Sees the sites at this unit and everything below it.</AccessRowMeta>
            </AccessRowText>
            <AccessRowActions>
              {canManage ? (
                <Button variant="text" size="small" onClick={() => setChanging(true)}>
                  Change unit
                </Button>
              ) : null}
            </AccessRowActions>
          </AccessRowShell>
        ) : !loaded ? null : placed ? (
          <AccessRowShell>
            <AccessRowText>
              <AccessRowTitleLine>
                <RolePicker />
              </AccessRowTitleLine>
              <AccessRowMeta>{`${details.name}'s unit has been deleted, so put them somewhere else.`}</AccessRowMeta>
            </AccessRowText>
            <AccessRowActions>
              {canManage ? (
                <Button variant="text" size="small" onClick={() => setChanging(true)}>
                  Set unit
                </Button>
              ) : null}
            </AccessRowActions>
          </AccessRowShell>
        ) : (
          <AccessRowShell>
            <AccessRowText>
              <AccessRowTitleLine>
                <RolePicker />
              </AccessRowTitleLine>
              <AccessRowMeta>{`${details.name} is not in a unit yet, so they see what they have always seen.`}</AccessRowMeta>
            </AccessRowText>
          </AccessRowShell>
        )}
      </RoleList>

      <RolesFooter>
        {canManage && loaded && hasStructure && !unitUuid ? (
          <Button variant="text" size="medium" onClick={() => setChanging(true)}>
            Put them in a unit
          </Button>
        ) : null}
        {canManage && loaded && !hasStructure ? (
          <Button
            variant="text"
            size="medium"
            onClick={() => navigate(String(routes.dashboard.settings.organisationStructure))}
          >
            Set up organisation structure
          </Button>
        ) : null}
        {isOwner ? null : <Muted>Only an Account Owner can change which unit someone is in.</Muted>}
        {isOwner && !details.isActive ? <Muted>Reactivate this user to change their unit.</Muted> : null}
      </RolesFooter>

      {changing && (
        <ChangeUnitModal
          userUuid={details.uuid}
          userName={details.name}
          currentUnitUuid={unitUuid}
          isAccountOwner={details.role === UserRole.AccountManager}
          onClose={() => setChanging(false)}
        />
      )}
    </RolesBlock>
  );
};

export default observer(RolesAtUnits);
